/**
 * @copyright (C) 2023, Rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of Rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by Rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @description
 *
 * @created at Sun Mar 05 2023
 **/
/* eslint @typescript-eslint/no-explicit-any: 0 */

import { useEffect, useRef, DependencyList } from "react";
import EventHandler from "./eventhandler";
import type { EventName, Handler } from "./eventhandler";

type Options = {
	disposing?: boolean;
};

const DefaultOptions: Options = {
	disposing: true
};

export default function useEventHandler(
	eventName: EventName,
	eventHandler: Handler,
	deps: DependencyList = [],
	options = DefaultOptions
) {
	const handler = EventHandler.find(eventName);
	const ref = useRef({
		needDispose: handler === null,
		handler
	});
	if (ref.current.handler === null) {
		ref.current = {
			...ref.current,
			handler: new EventHandler(eventName)
		};
	}

	useEffect(() => {
		ref.current.handler?.setEvents(eventName, eventHandler);
		return () => {
			ref.current.handler?.unsetEvents(eventName, eventHandler);
		};
	}, deps);

	useEffect(() => {
		return () => {
			if (options.disposing && ref.current.needDispose) {
				ref.current.handler?.dispose();
			}
		};
	}, []);

	return {
		send: (name: string, ...value: any[]) => {
			ref.current.handler?.send(name, ...value);
		}
	};
}
